/**
 * Mesh selectors in plain words: who a grant is for and what it reaches, as chips, and the
 * picker that adds one (people and groups from the directory, the autogroups, tags, kinds and
 * nodes). The selector strings are the contract's (`meshSelectorSchema`); nothing here invents one.
 */

import type { MeshNode, MeshNodeKind } from '@aioe/shared';
import { Button, Popover } from '@heroui/react';
import { Plus, Xmark } from '@gravity-ui/icons';
import { useMemo, useState } from 'react';

import { SubjectName, SubjectPicker } from './SubjectPicker.tsx';
import { hasDirectory } from '../directory.ts';
import { inputClass } from './ui.tsx';

/** The autogroups, as people say them. */
export const AUTOGROUP_TEXT: Record<string, string> = {
  'autogroup:owner': "Each person's nodes",
  'autogroup:self': "Each person's own nodes",
  'autogroup:operators': 'Operators',
  'autogroup:admins': 'Administrators',
  'autogroup:members': "Everyone's nodes",
  '*': 'Every node',
};

const KIND_TEXT: Record<MeshNodeKind, string> = {
  workbench: 'Workbenches',
  node: 'Headless nodes',
  mobile: 'Phones',
  gateway: 'The platform gateway',
  relay: 'Relays',
};

/** Which side of a grant the picker fills: the autogroups that make sense differ. */
export type Side = 'src' | 'dst';
const AUTOGROUPS: Record<Side, string[]> = {
  src: ['autogroup:owner', 'autogroup:operators', 'autogroup:admins', 'autogroup:members', '*'],
  dst: ['autogroup:self', 'autogroup:members', 'autogroup:operators', '*'],
};

/** A selector's words, for a chip or a sentence. */
export function SelectorText({ sel, nodes }: { sel: string; nodes: readonly MeshNode[] }) {
  if (AUTOGROUP_TEXT[sel]) return <>{AUTOGROUP_TEXT[sel]}</>;
  const i = sel.indexOf(':');
  const kind = sel.slice(0, i);
  const v = sel.slice(i + 1);
  switch (kind) {
    case 'tag':
      return (
        <>
          Tagged <span className="mono">{v}</span>
        </>
      );
    case 'kind':
      return <>{KIND_TEXT[v as MeshNodeKind] ?? v}</>;
    case 'node':
      return <>{nodes.find((n) => n.id === v)?.name ?? 'A removed node'}</>;
    case 'user': {
      const email = nodes.find((n) => n.ownerUserId === v)?.ownerEmail;
      return email ? <span title={v}>{email}</span> : <SubjectName id={v} />;
    }
    case 'group':
      return (
        <>
          Group <SubjectName id={v} />
        </>
      );
  }
  return <span className="mono">{sel}</span>;
}

/** One selector as a chip, with its own remove control. */
export function SelectorChip({ sel, nodes, onRemove }: { sel: string; nodes: readonly MeshNode[]; onRemove?: () => void }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-surface-secondary py-0.5 pl-2.5 pr-1 text-xs">
      <span className="truncate">
        <SelectorText sel={sel} nodes={nodes} />
      </span>
      {onRemove && (
        <button type="button" aria-label="Remove" className="grid size-4 shrink-0 cursor-[var(--cursor-interactive)] place-items-center rounded-full text-muted hover:bg-surface-tertiary hover:text-foreground" onClick={onRemove}>
          <Xmark className="size-3" />
        </button>
      )}
    </span>
  );
}

interface Option {
  sel: string;
  group: string;
  text: string;
  detail?: string;
}

/** What the picker offers: the autogroups for the side, then the tags, kinds, nodes and people the mesh knows. */
function optionsFor(side: Side, nodes: readonly MeshNode[]): Option[] {
  const out: Option[] = AUTOGROUPS[side].map((sel) => ({ sel, group: 'Everyone', text: AUTOGROUP_TEXT[sel]! }));
  const tags = [...new Set(nodes.flatMap((n) => [...n.tags, ...(n.requestedTags ?? [])]))].sort();
  for (const t of tags) out.push({ sel: `tag:${t}`, group: 'Tags', text: t, detail: `${nodes.filter((n) => n.tags.includes(t)).length} tagged` });
  for (const k of Object.keys(KIND_TEXT) as MeshNodeKind[]) out.push({ sel: `kind:${k}`, group: 'Kinds', text: KIND_TEXT[k] });
  for (const n of nodes) out.push({ sel: `node:${n.id}`, group: 'Nodes', text: n.name, detail: n.ownerEmail });
  if (side === 'src') {
    const owners = new Map<string, string | undefined>();
    for (const n of nodes) if (n.kind !== 'gateway' && n.kind !== 'relay' && !n.ownerUserId.startsWith('node:')) owners.set(n.ownerUserId, n.ownerEmail);
    for (const [id, email] of owners) out.push({ sel: `user:${id}`, group: 'People with nodes', text: email ?? id });
  }
  return out;
}

/** A small "Add" that opens the choices; picking one adds it and closes. */
export function SelectorPicker({ side, nodes, taken, onPick, label }: { side: Side; nodes: readonly MeshNode[]; taken: readonly string[]; onPick: (sel: string) => void; label: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const options = useMemo(() => optionsFor(side, nodes), [side, nodes]);
  const shown = options.filter((o) => !taken.includes(o.sel) && (!q || `${o.text} ${o.detail ?? ''}`.toLowerCase().includes(q.toLowerCase())));
  const groups = [...new Set(shown.map((o) => o.group))];
  const pick = (sel: string) => {
    onPick(sel);
    setOpen(false);
    setQ('');
  };
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Button size="sm" variant="ghost" aria-label={label} className="h-6 gap-1 px-2 text-xs">
        <Plus className="size-3.5" />
        Add
      </Button>
      <Popover.Content placement="bottom start" className="w-80 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="flex flex-col gap-2 p-2" aria-label={label}>
          {side === 'src' && hasDirectory() && <SubjectPicker kinds={['user', 'group']} placeholder="A person or group in the directory" onPick={(s) => pick(`${s.type}:${s.id}`)} />}
          <input className={inputClass} placeholder="Filter" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter the choices" />
          <div className="-m-1 max-h-72 overflow-y-auto p-1">
            {groups.map((g) => (
              <div key={g} className="mb-1">
                <span className="pane-label block px-2 pb-0.5 pt-1.5">{g}</span>
                <ul role="list">
                  {shown
                    .filter((o) => o.group === g)
                    .map((o) => (
                      <li key={o.sel}>
                        <button type="button" className="flex w-full cursor-[var(--cursor-interactive)] items-baseline justify-between gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-surface-secondary" onClick={() => pick(o.sel)}>
                          <span className={`truncate ${o.group === 'Tags' ? 'mono' : ''}`}>{o.text}</span>
                          {o.detail && <span className="shrink-0 truncate text-xs text-muted">{o.detail}</span>}
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
            {groups.length === 0 && <p className="px-2 py-1.5 text-sm text-muted">Nothing matches.</p>}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** The chips on one side of a grant or test, and the picker that adds to them. */
export function SelectorList({ side, value, nodes, onChange, label, single }: { side: Side; value: string[]; nodes: readonly MeshNode[]; onChange: (v: string[]) => void; label: string; single?: boolean }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {value.map((s) => (
        <SelectorChip key={s} sel={s} nodes={nodes} onRemove={() => onChange(value.filter((x) => x !== s))} />
      ))}
      {(!single || value.length === 0) && <SelectorPicker side={side} nodes={nodes} taken={value} label={label} onPick={(sel) => onChange(single ? [sel] : [...value, sel])} />}
      {value.length === 0 && !single && <span className="text-xs text-muted">Nobody yet</span>}
    </div>
  );
}
